// Sincronització dels events pendents amb el servidor (CURRENT_TASK.md
// §12-§13, §43-§44). Es processen sempre en ordre cronològic (mai en
// paral·lel): com que formen una seqüència d'estats (ENTRADA -> INICI_PAUSA
// -> ...), enviar-los desordenats podria fer que el backend rebutgi
// transicions vàlides. Si un pendent és rebutjat pel servidor, s'atura la
// resta de la cua (probablement en depenen) i es marca amb l'error perquè no
// quedi silenciat.
//
// No esborra mai un pendent d'IndexedDB fins que el servidor confirma
// aquell UUIDEvent concret (§12): idempotència pel costat servidor
// (backend/FitxatgeHelper.php::fitRegistrarEvent) + no esborrar localment
// fins a resposta positiva pel costat client.

import { chFetch, ChSessioCaducadaError, chObtenirUuidTerminal } from "./chAuth";
import { chLlistarPendents, chEsborrarPendent, chMarcarErrorPendent, chComptarPendents } from "./chIndexedDB";

let sincronitzacioEnCurs = false;

function construirCosPendent(pendent) {
  return {
    tipusEvent: pendent.tipusEvent,
    uuidEvent: pendent.uuidEvent,
    dataHoraEvent: pendent.dataHoraEvent,
    uuidTerminal: pendent.uuidTerminal || chObtenirUuidTerminal(),
    nomTerminal: pendent.nomTerminal || "",
    idTipusPausa: pendent.idTipusPausa || null,
    esOffline: true,
    estatGPS: pendent.estatGPS,
    latitud: pendent.latitud,
    longitud: pendent.longitud,
    precisioMetres: pendent.precisioMetres,
  };
}

export function chSincronitzacioEnCurs() {
  return sincronitzacioEnCurs;
}

// Retorna sempre un resum, mai llança excepcions (és cridat des de múltiples
// llocs -muntatge de pantalla, event "online", canvi de visibilitat, botó
// manual- i no ha de trencar cap d'aquests fluxos).
export async function chSincronitzarPendents() {
  if (sincronitzacioEnCurs) {
    return { sincronitzats: 0, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "en_curs" };
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { sincronitzats: 0, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "sense_connexio" };
  }

  sincronitzacioEnCurs = true;
  let sincronitzats = 0;
  try {
    let pendents;
    try {
      pendents = await chLlistarPendents();
    } catch {
      return { sincronitzats: 0, pendentsRestants: 0, aturat: true, motiu: "indexeddb_no_disponible" };
    }

    for (const pendent of pendents) {
      // Un pendent ja marcat amb un error del servidor requereix atenció
      // manual (§28, correccions administratives: Fase 4); no es reintenta
      // sol perquè probablement tornaria a fallar igual.
      if (pendent.estatLocal === "ERROR") {
        return { sincronitzats, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "rebutjat", missatge: pendent.ultimError };
      }

      let resposta;
      try {
        resposta = await chFetch("/ControlHorariFitxar.php", {
          method: "POST",
          body: JSON.stringify(construirCosPendent(pendent)),
        });
      } catch (err) {
        if (err instanceof ChSessioCaducadaError) {
          return { sincronitzats, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "sessio_caducada" };
        }
        return { sincronitzats, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "xarxa" };
      }

      const dades = await resposta.json().catch(() => null);
      if (dades && dades.success) {
        await chEsborrarPendent(pendent.uuidEvent);
        sincronitzats += 1;
      } else {
        const missatge = (dades && dades.message) || "El servidor ha rebutjat aquest registre.";
        await chMarcarErrorPendent(pendent.uuidEvent, missatge);
        return { sincronitzats, pendentsRestants: await chComptarPendents(), aturat: true, motiu: "rebutjat", missatge };
      }
    }

    return { sincronitzats, pendentsRestants: 0, aturat: false, motiu: null };
  } finally {
    sincronitzacioEnCurs = false;
  }
}
